import { AlertTriangle, X } from "lucide-react";
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "info";
}

interface ConfirmDialogContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmDialogContext = createContext<ConfirmDialogContextType | null>(null);

interface DialogState extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

export function ConfirmDialogProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [dialogState, setDialogState] = useState<DialogState | null>(null);
  const [isProcessing] = useState(false);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setDialogState({ ...options, resolve });
    });
  }, []);

  const handleClose = () => {
    if (!isProcessing && dialogState) {
      dialogState.resolve(false);
      setDialogState(null);
    }
  };

  const handleConfirm = () => {
    if (!dialogState) return;

    dialogState.resolve(true);
    setDialogState(null);
  };

  const getVariantClasses = (variant: "danger" | "warning" | "info" = "info") => {
    switch (variant) {
      case "danger":
        return {
          icon: "text-red-400",
          iconBg: "bg-red-500/20",
          button: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
        };
      case "warning":
        return {
          icon: "text-yellow-400",
          iconBg: "bg-yellow-500/20",
          button: "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500",
        };
      default:
        return {
          icon: "text-blue-400",
          iconBg: "bg-blue-500/20",
          button: "bg-brand-gradient hover:opacity-90",
        };
    }
  };

  const confirmValue = useMemo(() => ({ confirm }), [confirm]);

  if (!dialogState) {
    return <ConfirmDialogContext.Provider value={confirmValue}>{children}</ConfirmDialogContext.Provider>;
  }

  const variantClasses = getVariantClasses(dialogState.variant);

  return (
    <ConfirmDialogContext.Provider value={confirmValue}>
      {children}

      {/* Confirmation Dialog */}
      <div
        data-testid="confirm-dialog-overlay"
        className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4"
      >
        <div
          data-testid="confirm-dialog"
          className="bg-surface-primary rounded-xl border border-border-primary w-full max-w-md shadow-2xl"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-border-primary flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-lg ${variantClasses.iconBg} flex items-center justify-center`}>
                <AlertTriangle className={`h-5 w-5 ${variantClasses.icon}`} />
              </div>
              <h2 data-testid="confirm-dialog-title" className="text-lg font-semibold text-content-primary">
                {dialogState.title}
              </h2>
            </div>
            {!isProcessing && (
              <button onClick={handleClose} className="p-2 hover:bg-interactive-hover rounded-lg transition-colors">
                <X className="w-5 h-5 text-content-tertiary" />
              </button>
            )}
          </div>

          {/* Body */}
          <div className="px-6 py-4">
            <p data-testid="confirm-dialog-message" className="text-content-secondary">
              {dialogState.message}
            </p>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border-primary flex items-center gap-3 justify-end">
            <button
              data-testid="confirm-dialog-cancel"
              onClick={handleClose}
              disabled={isProcessing}
              className="px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {dialogState.cancelText || "Cancel"}
            </button>
            <button
              data-testid="confirm-dialog-confirm"
              onClick={handleConfirm}
              disabled={isProcessing}
              className={`px-4 py-2 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${variantClasses.button}`}
            >
              {dialogState.confirmText || "Confirm"}
            </button>
          </div>
        </div>
      </div>
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirm() {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error("useConfirm must be used within a ConfirmDialogProvider");
  }
  return context;
}
